import moment from "moment";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { Product } from "~/types/product";
import { ProductSummary } from "~/types/productSummary";

export const useProductStore = definePiniaStore("useProductStore", {
  state: () => ({
    _loading: true,
    _tableData: [] as Product[],
    _selectedProductForEdit: {} as Product,
    _filterData: {},
    _simpleFilter: {},
    // Direction the current screen last requested for the simple product list,
    // so a refresh (e.g. after creating a product inline) keeps that order.
    _simpleDir: "asc" as "asc" | "desc",
    _perPage: 50,
    // Bounds the API enforces; it reports them with every page so the input can
    // never ask for a size the server would silently clamp anyway.
    _minPerPage: 1,
    _maxPerPage: 100,
    _pagination: {} as Pagination,
    _allProducts: [] as Product[],
    _product: {} as Product,
    _productSummary: {} as ProductSummary,
    showEditModal: false,
    showCreateModal: false,
  }),
  getters: {
    tableData: (state) => state._tableData,
    selectedProductForEdit: (state) => state._selectedProductForEdit,
    pagination: (state) => state._pagination,
    allProducts: (state) => state._allProducts,
    perPage: (state) => state._perPage,
    minPerPage: (state) => state._minPerPage,
    maxPerPage: (state) => state._maxPerPage,
    // Sizes the picker offers, kept inside whatever bound the API reports.
    perPageOptions: (state) =>
      [10, 25, 50, 75, 100].filter(
        (size) => size >= state._minPerPage && size <= state._maxPerPage,
      ),
    product: (state) => state._product,
    productSummary: (state) => state._productSummary,
  },
  actions: {
    async getProducts(pageNo = 1, sortBy = "id", ascending = false) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `/products?page=${pageNo}&per_page=${this._perPage}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const key in this._filterData) {
        // @ts-ignore
        const value = this._filterData[key];
        if (value) url += `&${key}=${value}`;
        // @ts-ignore
        else delete this._filterData[key];
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Product>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this._loading = false;
        this.$state._pagination = pagination;
        if (pagination.min_per_page) {
          this.$state._minPerPage = pagination.min_per_page;
        }
        if (pagination.max_per_page) {
          this.$state._maxPerPage = pagination.max_per_page;
        }
      }
    },
    async exportTable() {
      let url = `/products/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"products-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    // `dir` orders by created_at. Defaults to "asc" (oldest first) so existing
    // callers are unaffected; pass "desc" for a newest-first dropdown.
    async getAllProducts(dir: "asc" | "desc" = "asc") {
      this.$state._simpleDir = dir;
      let url = `/products/simple?dir=${dir}&`;
      for (const key in this._simpleFilter) {
        // @ts-ignore
        url += `${key}=${this._simpleFilter[key]}&`;
      }
      const { response, error } = await getRequest<Product[]>(url);
      if (!error) {
        this.$state._allProducts = response.data;
      }
    },
    async getProduct(productId: number) {
      const { response, error } = await getRequest<Product>(
        `/products/${productId}`,
      );
      if (!error) {
        this.$state._product = response.data;
      }
    },
    async getProductSummary(productId: number) {
      const { response, error } = await getRequest<ProductSummary>(
        `/products/${productId}/crm-summary`,
      );
      if (!error) {
        this.$state._productSummary = response.data;
      }
    },
    async importProductsFromMeta() {
      const { response, error } = await postRequest<any>(
        "/products/import-products-from-meta",
        {},
      );
      if (!error) {
        return response.data;
      }
      return error.response?.data;
    },
    async createProduct(product: FormData, attachments: any[] = []) {
      attachments.forEach((attachment, index) => {
        product.append(`attachments[${index}][file]`, attachment.file);
        product.append(`attachments[${index}][name]`, attachment.name);
        product.append(
          `attachments[${index}][description]`,
          attachment.description ?? "",
        );
      });

      const { response, error } = await postRequest<any>(
        "/products",
        product,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (!error) {
        return response;
      }
      return null;
    },
    async updateProduct(id: number, product: any) {
      const { response, error } = await postRequest<any>(
        `/products/${id}`,
        product,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (!error) {
        return response.data;
      }
      return null;
    },
    async deleteProduct(product: Product) {
      return await deleteRequest(`/products/${product.id}`);
    },
    async skuExists(sku: string, productId: number | null = null) {
      let url = `/products/sku-exists?sku=${sku}`;

      if (productId != null) {
        url += `&id=${productId}`;
      }

      const { response, error } = await getRequest<boolean>(url);
      if (!error) {
        return response.data;
      }
    },

    // mutations
    setCreateModalVisibility(val: boolean) {
      this.$state.showCreateModal = val;
    },
    setEditModalVisibility(val: boolean) {
      this.$state.showEditModal = val;
    },
    setEditData(product: Product) {
      this.$state._selectedProductForEdit = product;
    },
    setPerPage(val: number) {
      const size = Number(val);
      this.$state._perPage = Number.isFinite(size)
        ? Math.min(
            Math.max(Math.trunc(size), this._minPerPage),
            this._maxPerPage,
          )
        : this._perPage;
    },
    setFilterData(filter: any) {
      this.$state._filterData = filter;
    },
    setSimpleFilter(filter: any) {
      this.$state._simpleFilter = filter;
    },
  },
});
